<template>
    <modal name="manage-cresiteiq-filters" width="800px" height="auto" draggable=".modal-header">
        <div class="flex flex-col">
            <div class="p-5 flex-1">
                <div class="flex items-center justify-between">
                    <h2 class="text-lg font-bold">CRESiteIQ&trade; Filters</h2>
                    <button @click="$modal.hide('manage-cresiteiq-filters')">
                        <inline-svg name="close"></inline-svg>
                    </button>
                </div>

                <div class="mt-5">
                    <!-- Tab Navigation -->
                    <div class="border-b border-gray-200">
                        <nav class="-mb-px flex space-x-8" aria-label="Tabs">
                            <button
                                @click="activeTab = 'metrics'"
                                :class="tabClass('metrics')"
                            >Metric Groups</button>
                            <button
                                @click="activeTab = 'poi'"
                                :class="tabClass('poi')"
                            >Point of Interest</button>
                        </nav>
                    </div>
                    <div class="mt-5 w-full">
                        <div class="flex gap-x-8" v-if="activeTab === 'metrics'">
                            <div class="bg-gray-50 py-4 px-6">
                                <ul class="flex flex-col gap-y-2">
                                    <li class="w-full" :class="{'bg-blue-100 font-semibold rounded-md': selectedMetricGroup === 'all'}">
                                        <button 
                                            @click="selectedMetricGroup = 'all'"
                                            class="w-full text-left px-4 py-2 hover:bg-gray-100 rounded-md"                                        
                                        >
                                            All
                                        </button>
                                    </li>
                                    <li v-for="(group, groupIndex) in availableMetricGroups" :key="groupIndex" class="w-full" :class="{'bg-blue-100 font-semibold rounded-md': selectedMetricGroup === group}">
                                        <button 
                                            @click="selectedMetricGroup = group"
                                            class="w-full text-left px-4 py-2 hover:bg-gray-100 rounded-md"                                        
                                        >
                                            {{ group.label }}
                                        </button>
                                    </li>
                                </ul>
                            </div>
                            <div class="w-full flex flex-col gap-y-2">
                                <div class="p-2 flex items-center w-full bg-gray-900 text-white font-semibold text-left text-sm uppercase">
                                    <span class="pl-4"></span>
                                    <span class="pl-4 flex-1">Metric</span>
                                    <span class="text-right pr-4">Weight (%)</span>
                                </div>
                                <!-- Search Input -->
                                <div class="px-4 pt-2">
                                    <input
                                        type="text"
                                        v-model="searchTerm"
                                        placeholder="Search metrics..."
                                        class="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                </div>
                                <div class="w-full max-h-[400px] min-h-[400px] overflow-y-auto">
                                    <table class="w-full">
                                        <tbody>
                                            <template v-if="filteredMetrics.length">
                                                <tr v-for="metric in filteredMetrics" :key="metric.slug">
                                                    <td class="pl-4 text-center max-w-xs">
                                                        <input
                                                            @change="toggleFilter($event, metric)"
                                                            class="w-4 h-4 mt-1"
                                                            type="checkbox"
                                                            :checked="creSiteIQ.selectedMetrics.includes(metric)"
                                                        >
                                                    </td>
                                                    <td class="pl-4">{{ metric.label }}</td>
                                                    <td class="text-right">
                                                        <input
                                                            @focus="$event.target.select()"
                                                            v-if="creSiteIQ.selectedMetrics.includes(metric)"
                                                            type="number"
                                                            v-model="metric.weight"
                                                            min="0"
                                                            max="100"
                                                            step="5"
                                                            size="5"
                                                        />
                                                        <span v-else>-</span>
                                                    </td>
                                                </tr>
                                            </template>
                                            <tr v-else class="text-center text-gray-500">
                                                <td colspan="3" class="py-4">No metrics found matching "{{ searchTerm }}"</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <div class="p-2 w-full flex items-center bg-gray-900 text-white font-semibold text-left text-sm uppercase">
                                    <span class="pl-4"></span>
                                    <span class="flex-1 text-right">Unallocated Percentage</span>
                                    <span class="px-4 text-right">{{ unallocatedPercentage }}%</span>
                                </div>
                            </div>
                        </div>
                        <!-- Point of Interest Tab -->
                        <div v-else-if="activeTab === 'poi'" class="text-gray-600 text-sm p-4">
                             <!-- POI Filters -->
                    
                        
                        <!-- Loading State -->
                        <div v-if="isLoadingPOITypes" class="py-2 text-sm text-gray-500">
                            Loading POI types...
                        </div>
                        
                        <!-- Error State -->
                        <div v-else-if="creSiteIQ.poi?.messages?.error" class="py-2 text-sm text-red-500">
                            {{ creSiteIQ.poi.messages.error }}
                        </div>
                        
                        <!-- Success State -->
                        <template v-else>
                            <div v-if="availablePOITypes.length > 0" class="flex flex-wrap gap-x-4 gap-y-2 gap-y-2 mt-2 max-h-[400px] min-h-[400px] overflow-y-auto">
                                <label 
                                    v-for="poi in availablePOITypes" 
                                    :key="poi.value" 
                                    class="flex items-center space-x-2 px-3 py-2 bg-gray-50 rounded-md hover:bg-gray-100 w-1/3"
                                >
                                    <input 
                                        type="checkbox" 
                                        :value="poi.value"
                                        v-model="selectedPOIs"
                                        class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" 
                                    />
                                    <span class="text-sm text-gray-700">{{ poi.label }}</span>
                                </label>
                            </div>
                            <div v-else class="py-2 text-sm text-gray-500">
                                No POI types available
                            </div>
                        </template>

                        </div>
                    </div>
                </div>
            </div>
            <div class="bg-gray-100 p-4">
                <div class="flex items-center justify-end gap-x-3">
                    <span v-if="error != null" class="text-red-500 font-semibold text-sm" v-text="error"></span>
                    <button @click.prevent="save" class="btn btn-green">
                        <span v-if="loading">
                            <inline-svg name="loading-button"></inline-svg>                                
                        </span>
                        <span v-if="! loading">Save &amp; Close</span>
                    </button>
                </div>
            </div>
        </div>
    </modal>
</template>

<script>
    import { mapState } from 'vuex';
    import InlineSvg from "../InlineSvg";

    export default {
        components: { InlineSvg },
        props: {
            initialSelectedPOIs: {
                type: Array,
                default: () => []
            }
        },
        emits: ['poi-updated'],

        data() {
            return {
                activeTab: 'metrics',
                selectedMetricGroup: 'all',
                selectedPOIs: [],
                error: null,
                loading: false,
                searchTerm: '',
            }
        },

        watch: {
            // Sync selectedPOIs with store when component is opened
            '$store.state.creSiteIQ.poi.selectedTypes': {
                immediate: true,
                handler(newSelectedTypes) {
                    if (newSelectedTypes && Array.isArray(newSelectedTypes)) {
                        this.selectedPOIs = [...newSelectedTypes];
                    } else {
                        this.selectedPOIs = [];
                    }
                }
            },
            'creSiteIQ.boundary': {
                handler() {
                    this.selectedMetricGroup = this.selectedMetricGroup == 'all' ? this.selectedMetricGroup : this.availableMetricGroups[0];
                },
            },
        },

        computed: {
            ...mapState(['creSiteIQ']),
            
            // Filter metrics based on search term and selected group
            filteredMetrics() {
                if (!this.searchTerm) {
                    return this.availableMetrics;
                }
                
                const searchTerm = this.searchTerm.toLowerCase();
                return this.availableMetrics.filter(metric => 
                    metric.label.toLowerCase().includes(searchTerm) ||
                    (metric.description && metric.description.toLowerCase().includes(searchTerm))
                );
            },
            // Get available POI types from the store
            availablePOITypes() {
                return this.creSiteIQ.poi?.availableTypes || [];
            },
            
            // Check if POI types are loading
            isLoadingPOITypes() {
                return this.creSiteIQ.poi?.isFetching || false;
            },

            availableMetricGroups() {
                if (this.creSiteIQ.boundary == 'zip') {
                    return _.reject(this.creSiteIQ.metricGroups, group => {
                        return ! _.filter(group.metrics, ['granularity', this.creSiteIQ.boundary]).length;
                    });
                } else if (this.creSiteIQ.boundary == 'state') {
                    return _.reject(this.creSiteIQ.metricGroups, group => {
                        return ! _.filter(group.metrics, ['granularity', this.creSiteIQ.boundary]).length;
                    });
                } else {
                    return this.creSiteIQ.metricGroups;
                }
            },

            availableMetrics() {
                let metrics = this.selectedMetricGroup == 'all' ? this.$store.getters.metrics : this.selectedMetricGroup?.metrics ?? [];

                if (this.creSiteIQ.boundary == 'zip') {
                    return _.filter(metrics, ['granularity', 'zip']);
                } else if (this.creSiteIQ.boundary == 'state') {
                    return _.filter(metrics, ['granularity', 'state']);
                }

                return _.sortBy(metrics, 'label');
            },

            unallocatedPercentage() {
                return 100 - parseInt(_.sumBy(this.$store.getters.metrics, (metric) => { return parseInt(metric.weight); }));
            },
        },
        async mounted() {
            // Initialize selectedPOIs from store or props
            const storePOIs = this.$store.state.creSiteIQ.poi?.selectedTypes || [];
            this.selectedPOIs = storePOIs.length ? [...storePOIs] : [...this.initialSelectedPOIs];
            
            // Load POI types if not already loaded
            if (this.availablePOITypes.length === 0) {
                this.$store.dispatch('fetchPlaceTypes');
            }
        },
        methods: {
            openModal(type) {
                this.activeTab = type;
                this.$modal.show('manage-cresiteiq-filters');
            },
            async applyPOIFilters() {
                if (Array.isArray(this.selectedPOIs)) {
                    this.$store.commit('setSelectedPOIs', this.selectedPOIs);
                    console.log('Selected POIs:', this.selectedPOIs);
                }
            },

            toggleFilter(event, metric) {
                let isOn = event.target.checked;

                if (isOn) {
                    metric.weight = this.unallocatedPercentage;
                    this.creSiteIQ.selectedMetrics.push(metric);
                } else {
                    metric.weight = 0;
                    this.creSiteIQ.selectedMetrics = _.filter(this.creSiteIQ.selectedMetrics, row => row.slug != metric.slug);
                }
            },

            async save() {
                this.error = null;

                if (this.creSiteIQ.selectedMetrics.length != 0 && this.unallocatedPercentage != 0) {
                    this.error = "Total weight for added filters must equal 100%";
                    return;
                }

                this.creSiteIQ.selectedMetrics.forEach(metric => {
                    if (metric.weight === 0) {
                        this.error = `Weight for ${metric.label} cannot be 0%`;
                        return false;
                    }
                });

                if (this.error == null) {
                    this.loading = true;

                    // Emit the updated POI selections
                    this.$emit('poi-updated', [...this.selectedPOIs]);
                    // Update the store with the selected POIs
                    this.$store.commit('setSelectedPOIs', this.selectedPOIs);
                    
                    await this.$store.dispatch('loadCRESiteIQData');
                    await this.$store.dispatch('refreshScores');

                    this.loading = false;
                    this.$modal.hide('manage-cresiteiq-filters');
                }
                
            },
            tabClass(tab) {
                return {
                    'border-b-2 border-blue-600 text-blue-600': this.activeTab === tab,
                    'border-b-2 border-gray-200 text-gray-600': this.activeTab !== tab
                };
            }
        }
    }
</script>

<style scoped>
/* Ensure the modal appears above map filters (z-index: 1000) */
.v--modal-overlay {
  z-index: 1001 !important;
}

.v--modal {
  z-index: 1002 !important;
}
</style>
